import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getChallengeById, ChallengeData } from '../services/challengeService';
import { getAvatarUrl, getStorageAvatarUrl } from '../utils/avatarUtils';
import { Swords, Trophy, Download, Share2, ArrowLeft, Clock, AlertCircle, Activity, BookOpen, Fingerprint, MessageSquare, CheckCircle, XCircle, X, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import html2canvas from 'html2canvas';
import ChallengeShareCard from '../components/social/ChallengeShareCard';
import ProvokeShareCard from '../components/social/ProvokeShareCard';
import { fetchChallengeLogs, ActivityLog } from '../services/activityLogService';
import { sendProvocation } from '../services/notificationService';
import { validateGrammar, ValidationResult } from '../services/grammarValidationService';
import { format } from 'date-fns';

export default function ChallengeDetailPage() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [challenge, setChallenge] = useState<ChallengeData | null>(null);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const shareCardRef = useRef<HTMLDivElement>(null);
  const provokeShareRef = useRef<HTMLDivElement>(null);
  const [challengerImgError, setChallengerImgError] = useState(false);
  const [challengedImgError, setChallengedImgError] = useState(false);

  const [provokeModalOpen, setProvokeModalOpen] = useState(false);
  const [provokeMessage, setProvokeMessage] = useState('');
  const [isTypingProvoke, setIsTypingProvoke] = useState(false);
  const [provokeValidation, setProvokeValidation] = useState<ValidationResult | null>(null);
  const [isCheckingProvoke, setIsCheckingProvoke] = useState(false);
  const [provokeSuccess, setProvokeSuccess] = useState(false);
  const [provokeContext, setProvokeContext] = useState('');
  const [pointOptionsModalOpen, setPointOptionsModalOpen] = useState(false);

  useEffect(() => {
    const fetchChallenge = async () => {
      if (challengeId) {
        try {
          const data = await getChallengeById(challengeId);
          setChallenge(data);
        } catch (error) {
          console.error("Failed to fetch challenge", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchChallenge();
  }, [challengeId]);

  useEffect(() => {
    const fetchLogs = async () => {
      if (challenge) {
        try {
          const data = await fetchChallengeLogs(
            [challenge.challengerId, challenge.challengedId],
            challenge.startTime,
            challenge.endTime || Date.now()
          );
          setLogs(data);
        } catch (error) {
          console.error("Failed to fetch logs", error);
        }
      }
    };
    if (challenge) {
      fetchLogs();
    }
  }, [challenge]);

  const isCompleted = challenge?.status === 'completed' || (challenge?.endTime && challenge.endTime < Date.now());
  const isChallenger = currentUser?.uid === challenge?.challengerId;
  const opponentId = isChallenger ? challenge?.challengedId : challenge?.challengerId;

  const currentProvokeOptions = React.useMemo(() => {
    if (!challenge || !currentUser) return [];
    
    const myScore = isChallenger ? (challenge.challengerScore || 0) : (challenge.challengedScore || 0);
    const theirScore = isChallenger ? (challenge.challengedScore || 0) : (challenge.challengerScore || 0);
    
    if (isCompleted) {
       if (challenge.winnerId === currentUser.uid) {
           return [
               "I won this time! Better luck next time! 🏆",
               "You did your best, but I was faster! ⚡",
               "Don't be sad, you can always try again! 😉",
               "That was a great match! Want a rematch? 🔄"
           ];
       } else if (challenge.winnerId) {
           return [
               "You got me this time, but I'll win the next one! 😤",
               "I'm coming for a rematch! ⚔️",
               "Good game, but I'll practice more! 📚",
               "Enjoy your victory, it won't last! 😏"
           ];
       } else {
           return [
               "A tie! We are equally matched! 🤝",
               "Wow, a draw! We need a tiebreaker! ⚖️",
               "Great minds think alike! 🧠",
               "Next time I'll make sure to win! 🚀"
           ];
       }
    } else {
        if (myScore > theirScore) {
            return [
               "I'm passing you on the leaderboard! 🏃‍♂️",
               "Catch me if you can! 💨",
               "You need to study more to beat me! 📚",
               "I'm winning this one! 🥇"
            ];
        } else if (myScore < theirScore) {
            return [
               "I'll try to win from you! 😤",
               "I'll do more to pass you! 💪",
               "Don't get too comfortable, I'm coming! 🏃‍♂️",
               "You're ahead now, but not for long! ⏳"
            ];
        } else {
            return [
               "It's a tie so far! Let's see who breaks it! ⚔️",
               "We are neck and neck! 🐎",
               "I'm going to pull ahead! 🚀",
               "This is getting intense! 😅"
            ];
        }
    }
  }, [challenge, currentUser, isCompleted, isChallenger]);

  const handleCheckProvoke = async () => {
    if (!provokeMessage.trim()) return;
    setIsCheckingProvoke(true);
    try {
        const result = await validateGrammar(provokeMessage);
        setProvokeValidation(result);
    } catch (e) {
        console.error(e);
    }
    setIsCheckingProvoke(false);
  };

  const getProvokeContextMessage = () => {
    if (!challenge) return '';
    const myName = currentUser?.displayName || 'Someone';
    const friendName = opponentId === challenge.challengerId ? challenge.challengerName : challenge.challengedName;
    const typeLabel = challenge.challengeType === 'crime_game' ? 'Crime Game' :
                    challenge.challengeType === 'daily_training' ? 'Training' :
                    challenge.challengeType === 'word_of_day' ? 'Word of the Day' : 'Points Race';
    
    if (isCompleted) {
        if (challenge.winnerId === currentUser?.uid) {
            return `${myName} poking ${friendName} after winning the ${typeLabel} challenge`;
        } else if (challenge.winnerId) {
            return `${myName} poking ${friendName} after losing the ${typeLabel} challenge`;
        } else {
            return `${myName} poking ${friendName} after a draw in the ${typeLabel} challenge`;
        }
    } else {
        return `${myName} poking ${friendName} during their ${typeLabel} challenge`;
    }
  };

  const handleSendCustomProvoke = async () => {
    if (!currentUser || !opponentId) return;
    
    if (!provokeValidation?.isCorrect) {
       await handleCheckProvoke();
       return;
    }
    
    try {
      await sendProvocation(currentUser.uid, opponentId, provokeMessage);
      setProvokeContext(getProvokeContextMessage());
      setProvokeSuccess(true);
    } catch (e) {
      console.error('Failed to send provocation:', e);
    }
  };
  
  const handleSendPresetProvoke = async (msg: string) => {
    if (!currentUser || !opponentId) return;
    try {
      await sendProvocation(currentUser.uid, opponentId, msg);
      setProvokeMessage(msg);
      setProvokeContext(getProvokeContextMessage());
      setProvokeSuccess(true);
    } catch (e) {
      console.error('Failed to send preset provocation:', e);
    }
  };

  const handleShareProvocation = async () => {
    if (!provokeShareRef.current) return;
    
    setTimeout(async () => {
      try {
        const canvas = await html2canvas(provokeShareRef.current!, { 
          useCORS: true,
          scale: 3, 
          backgroundColor: null,
          logging: false
        });
        
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          const file = new File([blob], `provocation.png`, { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({
                files: [file]
              });
            } catch (err) {
              console.log('Share cancelled or failed', err);
            }
          } else {
             const image = canvas.toDataURL("image/png");
             const link = document.createElement('a');
             link.href = image;
             link.download = `provocation.png`;
             link.click();
          }
        }, 'image/png', 1.0);
      } catch (e) {
        console.error("Failed to generate image", e);
      }
    }, 100);
  };

  const handleDownloadProvocation = async () => {
    if (!provokeShareRef.current) return;
    
    setTimeout(async () => {
      try {
        const canvas = await html2canvas(provokeShareRef.current!, { 
          useCORS: true,
          scale: 3, 
          backgroundColor: null,
          logging: false
        });
        
        const image = canvas.toDataURL("image/png", 1.0);
        const link = document.createElement('a');
        link.href = image;
        link.download = `provocation.png`;
        link.click();
      } catch (e) {
        console.error("Failed to generate image", e);
      }
    }, 100);
  };

  const handleProcessChallengeImage = async (action: 'share' | 'download' = 'share') => {
    if (!shareCardRef.current || !challenge) return;
    
    // Slight delay to ensure rendering is complete
    setTimeout(async () => {
      try {
        const imgs = shareCardRef.current!.querySelectorAll('img');
        imgs.forEach((img) => { 
          img.crossOrigin = 'anonymous'; 
        });

        const canvas = await html2canvas(shareCardRef.current!, { 
          useCORS: true,
          scale: 2, 
          backgroundColor: null,
          scrollX: 0,
          scrollY: 0,
          windowWidth: document.documentElement.scrollWidth,
          windowHeight: document.documentElement.scrollHeight
        });
        
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          
          if (action === 'share') {
            const file = new File([blob], `challenge-${challenge.id}.png`, { type: 'image/png' });
            
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              try {
                const myName = currentUser?.uid === challenge.challengerId ? challenge.challengerName : challenge.challengedName;
                await navigator.share({
                  title: 'LinguaLearn Challenge',
                  text: `Check out this challenge on LinguaLearn! Add me as a friend: ${myName || ''}\nhttps://lingualearn.web.app/challenges/${challenge.id}`,
                  files: [file]
                });
                return;
              } catch (err) {
                console.log('Share cancelled or failed', err);
              }
            }
          }
          
          // Download (fallback or explicit action)
          const image = canvas.toDataURL("image/png");
          const link = document.createElement('a');
          link.href = image;
          link.download = `challenge-${challenge.id}.png`;
          link.click();
        });
      } catch (e) {
        console.error("Failed to generate image", e);
      }
    }, 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Challenge not found</h2>
        <button 
          onClick={() => navigate('/challenges')}
          className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors"
        >
          Back to Challenges
        </button>
      </div>
    );
  }

  const typeLabel = challenge.challengeType === 'crime_game' ? 'Crime Game' :
                    challenge.challengeType === 'daily_training' ? 'Training' :
                    challenge.challengeType === 'word_of_day' ? 'Word of Day' : 'Point Race';

  const timeLeftMs = Math.max(0, challenge.endTime - Date.now());
  const hoursLeft = Math.floor(timeLeftMs / (1000 * 60 * 60));
  const minsLeft = Math.floor((timeLeftMs % (1000 * 60 * 60)) / (1000 * 60));
  
  const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 pb-24 md:pb-12">
      <button 
        onClick={() => navigate('/challenges')}
        className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 mb-8 transition-colors font-medium"
      >
        <ArrowLeft className="w-5 h-5" /> Back to Challenges
      </button>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100"
      >
        {/* Header Section */}
        <div className={`p-8 text-white ${isCompleted ? 'bg-gradient-to-r from-gray-800 to-gray-900' : 'bg-gradient-to-r from-indigo-600 to-purple-600'}`}>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              {isCompleted ? <Trophy className="w-8 h-8 text-yellow-400" /> : <Swords className="w-8 h-8 text-indigo-200" />}
              <h1 className="text-3xl font-black">{isCompleted ? 'Challenge Result' : 'Active Challenge'}</h1>
            </div>
            
            <div className="flex gap-2">
              <span className="px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-sm font-bold tracking-wider">
                {typeLabel.toUpperCase()} • {new Date(challenge.startTime).toLocaleDateString('en-GB')}
              </span>
              {!isCompleted && (
                <span className="px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-sm font-bold flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> {hoursLeft}h {minsLeft}m left
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Main Arena */}
        <div className="p-8 md:p-12 relative overflow-hidden bg-gray-50">
          <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, black 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
          
          <div className="relative flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
            
            {/* Challenger */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col items-center flex-1 w-full"
            >
              <div className="relative mb-6">
                <div 
                  onClick={() => navigate(`/profile/${challenge.challengerId}`)}
                  className={`w-32 h-32 md:w-40 md:h-40 rounded-full border-4 shadow-xl cursor-pointer hover:scale-105 transition-transform flex items-center justify-center bg-gradient-to-r from-blue-400 to-indigo-500 ${isCompleted && challenge.winnerId === challenge.challengerId ? 'border-yellow-400' : 'border-white'}`}
                >
                  {(challenge.challengerPhoto && !challengerImgError) ? (
                    <img referrerPolicy="no-referrer" 
                      src={challenge.challengerPhoto} 
                      className="w-full h-full object-cover rounded-full" 
                      alt={challenge.challengerName} 
                      onError={() => setChallengerImgError(true)}
                    />
                  ) : (
                    <span className="text-white text-5xl font-black drop-shadow-md">{getInitials(challenge.challengerName)}</span>
                  )}
                </div>
                {isCompleted && challenge.winnerId === challenge.challengerId && (
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-yellow-400 px-4 py-1.5 rounded-full shadow-lg border-2 border-yellow-200 z-20">
                    <span className="text-yellow-900 font-black text-sm uppercase tracking-widest whitespace-nowrap">Winner!</span>
                  </div>
                )}
                {isCompleted && challenge.winnerId === challenge.challengerId && (
                  <div className="absolute -top-4 -right-4 bg-yellow-400 p-2 rounded-full shadow-lg z-20">
                    <Trophy className="w-6 h-6 text-yellow-900" />
                  </div>
                )}
              </div>
              <h2 
                onClick={() => navigate(`/profile/${challenge.challengerId}`)}
                className="text-2xl font-bold text-gray-800 text-center cursor-pointer hover:text-indigo-600 transition-colors"
              >
                {challenge.challengerName}
              </h2>
              {isChallenger && <span className="text-xs font-bold text-indigo-500 uppercase tracking-widest bg-indigo-50 px-2 py-0.5 rounded-full mt-1">You</span>}
              
              <motion.div 
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                className="mt-6 bg-white px-8 py-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center w-full max-w-[200px]"
              >
                <span className="text-sm text-gray-500 font-medium uppercase tracking-wider mb-1">Score</span>
                <span className="text-5xl font-black text-indigo-600">{Math.round(challenge.challengerScore || 0)}</span>
              </motion.div>
            </motion.div>

            {/* VS Badge */}
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
              className="flex flex-col items-center justify-center shrink-0"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center shadow-inner border border-indigo-200">
                <span className="text-2xl font-black text-indigo-400 italic">VS</span>
              </div>
              {isCompleted && !challenge.winnerId && (
                <span className="mt-3 font-bold text-gray-500 uppercase tracking-widest text-sm bg-gray-200 px-3 py-1 rounded-full">Draw</span>
              )}
            </motion.div>

            {/* Challenged */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col items-center flex-1 w-full"
            >
              <div className="relative mb-6">
                <div 
                  onClick={() => navigate(`/profile/${challenge.challengedId}`)}
                  className={`w-32 h-32 md:w-40 md:h-40 rounded-full border-4 shadow-xl cursor-pointer hover:scale-105 transition-transform flex items-center justify-center bg-gradient-to-r from-pink-400 to-rose-500 ${isCompleted && challenge.winnerId === challenge.challengedId ? 'border-yellow-400' : 'border-white'}`}
                >
                  {(challenge.challengedPhoto && !challengedImgError) ? (
                    <img referrerPolicy="no-referrer" 
                      src={challenge.challengedPhoto} 
                      className="w-full h-full object-cover rounded-full" 
                      alt={challenge.challengedName} 
                      onError={() => setChallengedImgError(true)}
                    />
                  ) : (
                    <span className="text-white text-5xl font-black drop-shadow-md">{getInitials(challenge.challengedName)}</span>
                  )}
                </div>
                {isCompleted && challenge.winnerId === challenge.challengedId && (
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-yellow-400 px-4 py-1.5 rounded-full shadow-lg border-2 border-yellow-200 z-20">
                    <span className="text-yellow-900 font-black text-sm uppercase tracking-widest whitespace-nowrap">Winner!</span>
                  </div>
                )}
                {isCompleted && challenge.winnerId === challenge.challengedId && (
                  <div className="absolute -top-4 -right-4 bg-yellow-400 p-2 rounded-full shadow-lg z-20">
                    <Trophy className="w-6 h-6 text-yellow-900" />
                  </div>
                )}
              </div>
              <h2 
                onClick={() => navigate(`/profile/${challenge.challengedId}`)}
                className="text-2xl font-bold text-gray-800 text-center cursor-pointer hover:text-pink-600 transition-colors"
              >
                {challenge.challengedName}
              </h2>
              {!isChallenger && <span className="text-xs font-bold text-pink-500 uppercase tracking-widest bg-pink-50 px-2 py-0.5 rounded-full mt-1">You</span>}
              
              <motion.div 
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                className="mt-6 bg-white px-8 py-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center w-full max-w-[200px]"
              >
                <span className="text-sm text-gray-500 font-medium uppercase tracking-wider mb-1">Score</span>
                <span className="text-5xl font-black text-pink-600">{Math.round(challenge.challengedScore || 0)}</span>
              </motion.div>
            </motion.div>

          </div>
        </div>

        {/* Actions Footer */}
        <div className="p-6 md:p-8 bg-white border-t border-gray-100 flex flex-wrap justify-center gap-4">
          {!isCompleted ? (
             <button
                onClick={() => {
                  if (challenge.challengeType === 'crime_game') navigate('/crime-game');
                  else if (challenge.challengeType === 'daily_training') navigate('/training');
                  else if (challenge.challengeType === 'word_of_day') navigate('/day');
                  else setPointOptionsModalOpen(true);
                }}
                className="px-8 py-3.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <Swords className="w-5 h-5" /> Go Play
              </button>
          ) : (
             <button
                onClick={() => navigate('/social', { state: { challengeUserId: opponentId, activeTab: 'challenges' } })}
                className="px-8 py-3.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <Swords className="w-5 h-5" /> Rematch
              </button>
          )}
          
          <button 
            onClick={() => handleProcessChallengeImage('share')}
            className="px-6 py-3.5 bg-pink-50 text-pink-600 font-bold rounded-xl hover:bg-pink-100 transition-all border border-pink-100 flex items-center gap-2"
          >
            <Share2 className="w-5 h-5" /> Share
          </button>
          
          <button 
            onClick={() => handleProcessChallengeImage('download')}
            className="px-6 py-3.5 bg-blue-50 text-blue-600 font-bold rounded-xl hover:bg-blue-100 transition-all border border-blue-100 flex items-center gap-2"
          >
            <Download className="w-5 h-5" /> Download Image
          </button>

          <button 
            onClick={() => {
              setProvokeModalOpen(true);
              setProvokeMessage('');
              setIsTypingProvoke(false);
              setProvokeValidation(null);
              setProvokeSuccess(false);
              setProvokeContext('');
            }}
            className="px-6 py-3.5 bg-red-50 text-red-600 font-bold rounded-xl hover:bg-red-100 transition-all border border-red-100 flex items-center gap-2"
          >
            <AlertCircle className="w-5 h-5" /> Provoke
          </button>
        </div>
      </motion.div>

      {/* Activity Feed */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-8 bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100"
      >
        <div className="p-6 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
          <Activity className="w-6 h-6 text-indigo-500" />
          <h3 className="text-xl font-bold text-gray-800">Activity Feed</h3>
        </div>
        
        <div className="p-6">
          {logs.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No activities recorded yet. Start playing!</p>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => {
                const isChallengerLog = log.userId === challenge.challengerId;
                const iconBgClass = log.activityType === 'crime_game' ? 'bg-orange-100 text-orange-600' :
                                   log.activityType === 'word_of_day' ? 'bg-green-100 text-green-600' :
                                   'bg-indigo-100 text-indigo-600';
                
                return (
                  <div key={log.id} className="flex items-center gap-4 p-4 rounded-2xl hover:bg-gray-50 transition-colors border border-gray-100">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${iconBgClass}`}>
                      {log.activityType === 'crime_game' ? <Fingerprint className="w-6 h-6" /> : <BookOpen className="w-6 h-6" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-800 font-medium truncate">
                        <span className={isChallengerLog ? 'text-indigo-600 font-bold' : 'text-pink-600 font-bold'}>{log.userName}</span>
                        {' '}completed {
                          log.activityType === 'crime_game' ? 'a Crime Game' :
                          log.activityType === 'training' || log.activityType === 'daily_training' ? 'a Training session' :
                          log.activityType === 'word_of_day' ? 'a Word of the Day challenge' :
                          'an activity'
                        }
                      </p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(log.timestamp), "dd/MM/yyyy 'at' HH:mm")}
                        {log.word && <span className="text-gray-500 italic"> • {log.word}</span>}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => {
                        if (log.activityType === 'crime_game') navigate('/crime-game');
                        else if (log.activityType === 'training' || log.activityType === 'daily_training') navigate('/training');
                        else if (log.activityType === 'word_of_day') navigate('/day');
                      }}
                      className="shrink-0 bg-indigo-50 text-indigo-600 px-4 py-2 rounded-xl text-sm font-bold hover:bg-indigo-100 transition-colors"
                    >
                      {log.activityType === 'crime_game' ? 'Play Game' :
                       log.activityType === 'training' || log.activityType === 'daily_training' ? 'Do Training' :
                       'Word of Day'}
                    </button>

                    <div className="shrink-0 flex items-center justify-center bg-gray-100 px-4 py-2 rounded-xl">
                      <span className="font-bold text-gray-800">+{log.pointsEarned}</span>
                      <span className="text-xs text-gray-500 ml-1">pts</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>

      {/* Hidden Share Card for Image Generation */}
      <ChallengeShareCard challenge={challenge} currentUser={currentUser} cardRef={shareCardRef} />
      
      <ProvokeShareCard 
        currentUser={currentUser} 
        friend={{
          id: opponentId || '',
          username: isChallenger ? challenge.challengedName : challenge.challengerName,
          profilePhotoUrl: isChallenger ? challenge.challengedPhoto : challenge.challengerPhoto
        }} 
        provokeMessage={provokeMessage} 
        provokeContext={provokeContext} 
        cardRef={provokeShareRef} 
      />

      {/* Provoke Modal */}
      {provokeModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl relative">
            <button 
              onClick={() => setProvokeModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            {provokeSuccess ? (
              <div className="text-center py-4">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Provocation Sent!</h3>
                <p className="text-gray-500 mb-8">Your friend will get a notification to wake up and study.</p>
                <div className="flex flex-col gap-3">
                  <button 
                    onClick={handleShareProvocation}
                    className="w-full py-3.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex justify-center items-center gap-2"
                  >
                    <Share2 className="w-5 h-5" /> Share on Social Media
                  </button>
                  <button 
                    onClick={handleDownloadProvocation}
                    className="w-full py-3.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex justify-center items-center gap-2"
                  >
                    <Download className="w-5 h-5" /> Download Image
                  </button>
                  <button 
                    onClick={() => setProvokeModalOpen(false)}
                    className="w-full py-3.5 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Flame className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Send Provocation</h3>
                  <p className="text-gray-500 text-sm">Choose a message or write your own to wake them up!</p>
                </div>
                
                {!isTypingProvoke ? (
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1 scrollbar-hide">
                    {currentProvokeOptions.map((msg, index) => (
                      <button 
                        key={index}
                        onClick={() => handleSendPresetProvoke(msg)}
                        className="w-full flex items-center p-4 rounded-xl border-2 border-gray-100 hover:border-red-400 hover:bg-red-50 transition-all text-left group"
                      >
                        <span className="font-medium text-gray-800 group-hover:text-red-700">{msg}</span>
                      </button>
                    ))}
                    
                    <button 
                      onClick={() => setIsTypingProvoke(true)}
                      className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-600 transition-all"
                    >
                      <MessageSquare className="w-5 h-5" />
                      <span className="font-medium">Write my own (in English)</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 scrollbar-hide">
                    <textarea
                      value={provokeMessage}
                      onChange={(e) => {
                        setProvokeMessage(e.target.value);
                        setProvokeValidation(null);
                      }}
                      placeholder="Type your provocation here in English..."
                      className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none resize-none h-32"
                    />
                    
                    {provokeValidation && (
                      <div className={`p-4 rounded-xl text-sm ${provokeValidation.isCorrect ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                        <div className="flex items-start gap-2">
                          {provokeValidation.isCorrect ? <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 shrink-0 mt-0.5" />}
                          <div>
                            <p className="font-medium">{provokeValidation.feedback}</p>
                            {provokeValidation.errors && provokeValidation.errors.length > 0 && (
                              <ul className="mt-2 space-y-1 list-disc list-inside opacity-90">
                                {provokeValidation.errors.map((err: any, i: number) => (
                                  <li key={i}>{err.message}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setIsTypingProvoke(false)}
                        className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                      >
                        Back
                      </button>
                      <button 
                        onClick={provokeValidation?.isCorrect ? handleSendCustomProvoke : handleCheckProvoke}
                        disabled={isCheckingProvoke || !provokeMessage.trim()}
                        className="flex-[2] px-4 py-3 rounded-xl bg-indigo-600 text-white font-medium shadow-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 flex items-center justify-center gap-2"
                      >
                        {isCheckingProvoke ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : provokeValidation?.isCorrect ? (
                          'Send Provocation'
                        ) : (
                          'Check Grammar'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Point Options Modal */}
      {pointOptionsModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl relative">
            <button 
              onClick={() => setPointOptionsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Earn Points</h3>
              <p className="text-gray-500 text-sm">Choose an activity to play and earn points!</p>
            </div>
            
            <div className="space-y-3">
              <button 
                onClick={() => navigate('/training')}
                className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 hover:border-blue-500 hover:bg-blue-50 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <BookOpen className="w-6 h-6 text-blue-500" />
                  <div className="text-left">
                    <p className="font-bold text-gray-800">Daily Training</p>
                    <p className="text-xs text-gray-500">Practice your vocabulary</p>
                  </div>
                </div>
              </button>
              
              <button 
                onClick={() => navigate('/crime-game')}
                className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 hover:border-purple-500 hover:bg-purple-50 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <Fingerprint className="w-6 h-6 text-purple-500" />
                  <div className="text-left">
                    <p className="font-bold text-gray-800">Crime Game</p>
                    <p className="text-xs text-gray-500">Solve mysteries</p>
                  </div>
                </div>
              </button>
              
              <button 
                onClick={() => navigate('/day')}
                className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 hover:border-amber-500 hover:bg-amber-50 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <Activity className="w-6 h-6 text-amber-500" />
                  <div className="text-left">
                    <p className="font-bold text-gray-800">Word of the Day</p>
                    <p className="text-xs text-gray-500">Learn a new word</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
